import { QueryBuilder } from 'vet-bones/utils/queryBuilder';

import { DataSourceType } from 'src/constants/dataSourceTypeDescription';
import { ProjectResourceType } from 'src/constants/project';
import {
  HiddenCategory,
  LinkCategory,
  LinkLabelCategory,
  NodeCategory,
} from 'src/containers/ProjectDiagram/constants/categories';

// base data

export interface BaseData {
  key: string;
  value: string;
  category: NodeCategory | LinkLabelCategory | LinkCategory | HiddenCategory;

  hidden?: boolean;
}

export interface BaseNodeData extends BaseData {
  // key is `${category}-${value}`
  category: NodeCategory;

  pos?: string;
}

export interface BaseLinkLabelData extends BaseData {
  // key is `${category}-${from}-${value}-${to}`
  category: LinkLabelCategory;

  from?: string;
  to?: string;
}

export interface BaseLinkData extends BaseData {
  // key is `${category}-${from}-${value}-${to}`
  category: LinkCategory;

  from?: string;
  to?: string;
  labelKeys: string[];

  points?: number[] | go.List<go.Point>;
}

export interface HiddenData extends Omit<BaseData, 'value'> {
  value: string | null;
  category: HiddenCategory;
}

// resources

type ResourceField = {
  label: string;
  value: string;
};

export interface Resource extends BaseNodeData {
  category: NodeCategory.RESOURCE;
  type: ProjectResourceType;

  fields: ResourceField[];
  text: string;
}

export interface FileUpload extends Resource {
  type: ProjectResourceType.FILE_UPLOAD;
}

export interface VirtualGraph extends Resource {
  type: ProjectResourceType.VIRTUAL_GRAPH;
  dataSourceType: DataSourceType;
}

// mappings

export interface MappingLabel extends BaseLinkLabelData {
  category: LinkLabelCategory.MAPPING_LABEL;
  from: string;

  text: string;
}

export type MappingField = {
  label: string;
  value: string;
  targets: string[];
};

export enum MappingIdentifierType {
  DELIMITER = 'delimiter',
  FIELD = 'field',
}

export type MappingIdentifierPart = {
  type: MappingIdentifierType;
  value: string;
};

export type MappingIdentifier = {
  baseIri: string;
  parts: MappingIdentifierPart[];
};

export interface Mapping extends BaseLinkData {
  category: LinkCategory.MAPPING;
  from: string;

  text: string;
  primaryIdentifier: MappingIdentifier;
  label: string | null;
  fields: Record<string, MappingField>;
}

export type RelationshipMapping =
  | (BaseLinkData & {
      // while conceptually a link, a relationship mapping
      // will not have labels or positions
      category: LinkCategory.RELATIONSHIP_MAPPING;
      relationshipIri: string;

      from?: never;
      fromClassIri?: string;
      fromPrimaryIdentifier?: MappingIdentifier;

      to: string;
      toClassIri?: never;
      toPrimaryIdentifier?: never;
    })
  | (BaseLinkData & {
      // while conceptually a link, a relationship mapping
      // will not have labels or positions
      category: LinkCategory.RELATIONSHIP_MAPPING;
      relationshipIri: string;

      from: string;
      fromClassIri?: never;
      fromPrimaryIdentifier?: never;

      to?: never;
      toClassIri?: string;
      toPrimaryIdentifier?: MappingIdentifier;
    });

export const enum MappingType {
  CLASS = 'class',
  RELATIONSHIP = 'relationship',
  UNCONNECTED = 'unconnected',
  NONE = 'none',
}

// model concepts

export interface ModelConceptAttributeData {
  value: string;
  models: string[];
}

export interface ModelConceptAttribute {
  value: string;
  domains: Record<string, ModelConceptAttributeData>;
  ranges: Record<string, ModelConceptAttributeData>;
  models: string[];
}

export const enum ModelConceptClassHierarchy {
  CHILDREN = 'children',
  PARENTS = 'parents',
}

export interface ModelConceptClass extends BaseNodeData {
  category: NodeCategory.MODEL_CONCEPT_CLASS;

  isAttributesOpen: boolean;
  models: string[];
}

export interface ModelConceptRelationshipLabel extends BaseLinkLabelData {
  category: LinkLabelCategory.MODEL_CONCEPT_RELATIONSHIP_LABEL;
}

export interface ModelConceptRelationship extends BaseLinkData {
  category: LinkCategory.MODEL_CONCEPT_RELATIONSHIP;

  models: string[];
}

export interface ModelConceptHierarchyLabel extends BaseLinkLabelData {
  category: LinkLabelCategory.MODEL_CONCEPT_HIERARCHY_LABEL;
  from: string;
  to: string;
}
export interface ModelConceptHierarchy extends BaseLinkData {
  category: LinkCategory.MODEL_CONCEPT_HIERARCHY;
  from: string;
  to: string;

  models: string[];
}

export interface ModelConceptHiddenAttribute extends HiddenData {
  category: HiddenCategory.MODEL_CONCEPT_ATTRIBUTE;
}

// other model data

export type ModelRule = {
  id: string;
  name: string;
  ifClause: QueryBuilder;
  thenClause: QueryBuilder;
};
